import React, { FC, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ReactComponent as NavArrowIcon } from 'src/assets/images/trainer/clients/nav-arrow-icon.svg';
import { ReactComponent as WorkoutsSummaryIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/workouts-summary.svg';
import { ReactComponent as WorkoutDurationIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/workout-duration.svg';
import {
  ReactComponent as ExercisesIcon,
  ReactComponent as WorkoutExercisesIcon,
} from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/exercises.svg';
import { ReactComponent as CaloriesEstIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/calories-est.svg';
import { ReactComponent as PerformanceOverviewIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/performance-overview.svg';
import {
  ReactComponent as AiObservationsIcon,
  ReactComponent as AiObservationIcon,
} from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/ai-observations.svg';
import { ReactComponent as MuscleFocusIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/muscle-focus.svg';
import { ReactComponent as WorkoutMetricsIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/workout-metrics.svg';
import { ReactComponent as AvgHeartRateIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/avg-heart-rate.svg';
import { ReactComponent as TotalRepsIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/total-reps.svg';
import { ReactComponent as VolumeIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/volume.svg';
import { ReactComponent as RestTimeIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/rest-time.svg';

import { ReactComponent as PostureIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/posture.svg';
import { ReactComponent as DepthIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/depth.svg';
import { ReactComponent as KneeAlignmentIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/knee-alignment.svg';
import { ReactComponent as CoreStabilityIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/core-stability.svg';

import { MuscleGroupImages } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGeneratorAI/assets/MuscleGroupImages';
import { useGetAllMovements } from 'src/api/queries/useGetAllMovements';
import { useGetClientWorkoutDetails } from 'src/api/queries/useGetClientWorkoutDetails';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { isFemaleProfile } from 'src/utils/genderUtils';

import './WorkoutDetails.scss';

export const WorkoutDetails: FC = () => {
  const { uid, workoutId } = useParams<{ uid: string; workoutId: string }>();
  const navigate = useNavigate();
  const { client } = useOutletContext<{ client: any }>();

  const { data: workoutDetails, isLoading: isDetailsLoading } = useGetClientWorkoutDetails({
    clientId: uid || '',
    workoutId: workoutId || '',
  });

  const { data: programMasterData } = useGetProgramMasterData();

  const [expandedExercise, setExpandedExercise] = useState<number | null>(2);

  const toggleExercise = (index: number) => {
    setExpandedExercise(expandedExercise === index ? null : index);
  };

  const { data: allMovements = [] } = useGetAllMovements();

  const normalize = (str: string) => (str || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const isFemale = isFemaleProfile(workoutDetails || client);

  const getExerciseImageUrl = (apiEx: any) => {
    if (isFemale && apiEx.image_female_url) return apiEx.image_female_url;
    if (!isFemale && apiEx.image_male_url) return apiEx.image_male_url;
    if (apiEx.image_male_url || apiEx.image_female_url) {
      return isFemale
        ? apiEx.image_female_url || apiEx.image_male_url
        : apiEx.image_male_url || apiEx.image_female_url;
    }

    const normTitle = normalize(apiEx.title || apiEx.name || '');
    const matched = allMovements.find(
      (m: any) =>
        normalize(m.id || m._id) === normTitle || normalize(m.en_display_name) === normTitle,
    );

    if (matched) {
      return isFemale
        ? matched.image_female_url || matched.image_male_url
        : matched.image_male_url || matched.image_female_url;
    }

    return '';
  };

  const apiExercises = workoutDetails?.workoutExercises || [];

  const getSlugTitle = (slug: string): string => {
    if (!slug) return '';
    if (!programMasterData) {
      return slug
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    let result: string | null = null;
    const search = (obj: any) => {
      if (result) return;
      if (!obj || typeof obj !== 'object') return;

      const itemSlug = obj.slug || obj.id;
      if (itemSlug && typeof itemSlug === 'string') {
        const normalizedObjSlug = itemSlug.replace(/[-_]/g, '_').toLowerCase();
        const normalizedTargetSlug = slug.replace(/[-_]/g, '_').toLowerCase();
        if (normalizedObjSlug === normalizedTargetSlug && obj.title) {
          result = obj.title;
          return;
        }
      }

      if (Array.isArray(obj)) {
        obj.forEach(search);
      } else {
        Object.values(obj).forEach(search);
      }
    };
    search(programMasterData);

    if (result) return result;

    return slug
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const primaryGoalTitle = workoutDetails?.primaryGoal
    ? workoutDetails.primaryGoal
        .split(',')
        .map((s: string) => getSlugTitle(s.trim()))
        .filter(Boolean)
        .join(', ')
    : 'Not specified';

  const secondaryGoalTitles = workoutDetails?.secondaryGoals?.length
    ? workoutDetails.secondaryGoals
        .map((slug: string) => getSlugTitle(slug))
        .filter(Boolean)
        .join(', ')
    : 'Not specified';

  let formattedRestTime = '0s';
  if (workoutDetails?.workoutMetrics) {
    const totalSecs = workoutDetails.workoutMetrics.restTimeSecs || 0;
    if (totalSecs >= 60) {
      const mins = Math.floor(totalSecs / 60);
      const secs = totalSecs % 60;
      formattedRestTime = secs > 0 ? `${mins}m ${secs}s` : `${mins} min`;
    } else {
      formattedRestTime = `${totalSecs}s`;
    }
  }

  const getMuscleKey = (group: string): string => {
    const normalized = group.toLowerCase().replace(/-/g, '_');
    if (normalized.includes('full_body') || normalized.includes('total_body')) return 'full_body';
    if (
      normalized.includes('upper_body') ||
      normalized.includes('shoulder') ||
      normalized.includes('chest') ||
      normalized.includes('arm') ||
      normalized.includes('bicep') ||
      normalized.includes('tricep') ||
      normalized.includes('wrist') ||
      normalized.includes('neck') ||
      normalized.includes('elbow')
    ) {
      return 'upper_body';
    }
    if (
      normalized.includes('lower_body') ||
      normalized.includes('leg') ||
      normalized.includes('quad') ||
      normalized.includes('hamstring') ||
      normalized.includes('knee') ||
      normalized.includes('ankle') ||
      normalized.includes('calf') ||
      normalized.includes('calves') ||
      normalized.includes('feet') ||
      normalized.includes('foot')
    ) {
      return 'lower_body';
    }
    if (normalized.includes('back') || normalized.includes('spine') || normalized.includes('lat')) {
      return 'back';
    }
    if (
      normalized.includes('core') ||
      normalized.includes('abs') ||
      normalized.includes('abdominal')
    ) {
      return 'core';
    }
    if (normalized.includes('glute') || normalized.includes('hip')) {
      return 'glutes';
    }
    return normalized;
  };

  const bodyProfile =
    workoutDetails?.clientBodyProfile?.toLowerCase() === 'male' ? 'male' : 'female';
  const muscleGroups = workoutDetails?.muscleGroups || [];

  const exercises = apiExercises.map((apiEx: any) => {
    let statusClass = 'upcoming';
    if (apiEx.status === 'Completed') {
      if (apiEx.formLabel === 'Great') statusClass = 'great';
      else if (apiEx.formLabel === 'Good') statusClass = 'good';
      else statusClass = 'needs-focus';
    }

    const sets = apiEx.setsCount || 0;
    const reps = apiEx.repsOrDurationLabel || '-';

    let formText = 'Upcoming...';
    if (apiEx.status === 'Completed') {
      if (apiEx.formLabel === 'Needs Focus' || apiEx.formLabel === 'Bad') {
        formText = 'Needs Focus';
      } else if (apiEx.formLabel) {
        formText = `${apiEx.formLabel} Form`;
      } else {
        formText = 'Completed';
      }
    }

    return {
      name: apiEx.title,
      duration: `${sets} × ${reps}`,
      score: apiEx.formScore,
      form: formText,
      status: statusClass,
      image: getExerciseImageUrl(apiEx),
      details: apiEx.details,
    };
  });

  let isCompleted = false;
  if (workoutDetails?.ymd) {
    const ymd = workoutDetails.ymd;
    const selectTime = workoutDetails.selectTime || '00:00';
    const workoutDateTime = dayjs(`${ymd}T${selectTime}:00`);
    if (workoutDateTime.isBefore(dayjs())) {
      isCompleted = true;
    }
  }

  return (
    <div className="workout-details-page">
      <div className="wd-header">
        <div className="header-left">
          <button
            className="back-btn"
            onClick={() => navigate(`/onboarding-trainer/dashboard/clients/${uid}/overview`)}
          >
            <NavArrowIcon className="back-icon" />
          </button>
          <h1>
            <span className="title-main">
              {workoutId}
              <sup>
                {workoutId === '1'
                  ? 'st'
                  : workoutId === '2'
                    ? 'nd'
                    : workoutId === '3'
                      ? 'rd'
                      : 'th'}
              </sup>{' '}
              program&apos;s workout
            </span>
            {workoutDetails?.dateLabel && (
              <span className="subtitle">• {workoutDetails.dateLabel}</span>
            )}
          </h1>
        </div>
        <div className={`status-badge ${isCompleted ? 'completed' : 'in-progress'}`}>
          {isCompleted ? 'COMPLETED' : 'IN PROGRESS...'}
        </div>
      </div>

      {isDetailsLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#fff' }} spin />} />
        </div>
      ) : (
        <div className="wd-content-grid">
          {/* Workouts Summary */}
          <div className="wd-card workouts-summary-card">
            <div className="wd-card-header">
              <WorkoutsSummaryIcon />
              <h2>Workouts summary</h2>
            </div>
            <div className="wd-card-content">
              <div className="summary-item">
                <WorkoutDurationIcon />
                <span>Workout duration</span>
                <span className="value">
                  {isDetailsLoading
                    ? '...'
                    : workoutDetails?.durationMin
                      ? `${workoutDetails.durationMin} min`
                      : '-'}
                </span>
              </div>
              <div className="summary-item">
                <ExercisesIcon />
                <span>Exercises</span>
                <span className="value">
                  {isDetailsLoading ? '...' : workoutDetails?.exercisesCount || 0}
                </span>
              </div>
              <div className="summary-item">
                <CaloriesEstIcon />
                <span>Calories (est.)</span>
                <span className="value">
                  {isDetailsLoading
                    ? '...'
                    : workoutDetails?.caloriesEst
                      ? `${workoutDetails.caloriesEst} kcal`
                      : '-'}
                </span>
              </div>
            </div>
          </div>

          <div className="wd-card mobile-only ai-observations-card">
            <div className="wd-card-header">
              <AiObservationsIcon />
              <h2>AI Observations</h2>
            </div>
            <div className="wd-card-content overarching-ai">
              <p
                style={{
                  color: 'var(--auth-text-muted)',
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: '22px',
                }}
              >
                {workoutDetails?.aiObservations || 'No Data'}
              </p>
            </div>
          </div>

          {/* Performance Overview */}
          <div className="wd-card performance-overview-card">
            <div className="wd-card-header">
              <PerformanceOverviewIcon />
              <h2>Performance Overview</h2>
            </div>
            <div className="wd-card-content performance-content">
              <div className="donut-chart-container">
                {/* CSS Donut Chart */}
                <div
                  className="donut-ring"
                  style={{
                    background: `conic-gradient(from 140deg, ${
                      workoutDetails?.performanceOverview?.colorHex || '#7bc14f'
                    } ${
                      ((workoutDetails?.performanceOverview?.overallScore || 0) / 100) * 360
                    }deg, #00000033 ${
                      ((workoutDetails?.performanceOverview?.overallScore || 0) / 100) * 360
                    }deg)`,
                  }}
                >
                  <div className="donut-inner">
                    <span
                      className="donut-score"
                      style={{ color: workoutDetails?.performanceOverview?.colorHex || '#7bc14f' }}
                    >
                      {workoutDetails?.performanceOverview?.overallScore || 0}%
                    </span>
                    <span
                      className="donut-label"
                      style={{ color: workoutDetails?.performanceOverview?.colorHex || '#7bc14f' }}
                    >
                      Overall Score
                    </span>
                  </div>
                </div>
              </div>
              <div className="performance-legend">
                <div className="legend-item">
                  <span className="dot dot-great"></span> Great Form{' '}
                  <b>{workoutDetails?.performanceOverview?.greatCount || 0}</b>
                </div>
                <div className="legend-item">
                  <span className="dot dot-good"></span> Good Form{' '}
                  <b>{workoutDetails?.performanceOverview?.goodCount || 0}</b>
                </div>
                <div className="legend-item">
                  <span className="dot dot-needs-focus"></span> Needs Focus{' '}
                  <b>
                    {workoutDetails?.performanceOverview?.needsFocusCount ??
                      workoutDetails?.performanceOverview?.badCount ??
                      0}
                  </b>
                </div>
                <div className="legend-item">
                  <span className="dot dot-skipped"></span> Skipped{' '}
                  <b>{workoutDetails?.performanceOverview?.skippedCount || 0}</b>
                </div>
              </div>
            </div>
          </div>

          {/* Workout Exercises */}
          <div className="wd-card workout-exercises-card">
            <div className="wd-card-header">
              <WorkoutExercisesIcon className="workout-exercises-icon" />
              <h2>Workout Exercises</h2>
            </div>
            <div className="wd-card-content">
              <div className="exercise-list">
                {exercises.map((ex, index) => (
                  <div
                    key={index}
                    className={`exercise-item ${expandedExercise === index ? 'expanded' : ''}`}
                  >
                    <div className="exercise-header" onClick={() => toggleExercise(index)}>
                      {ex.image ? (
                        <img src={ex.image} alt={ex.name} className="ex-img" />
                      ) : (
                        <div className="ex-img">{(ex.name || 'EX').substring(0, 2)}</div>
                      )}
                      <div className="ex-header-right">
                        <div className="ex-text">
                          <span className="ex-name">{ex.name}</span>
                          <span className="ex-dur">{ex.duration}</span>
                        </div>
                        <div className="ex-score-box">
                          {ex.score !== undefined && ex.score !== null ? (
                            <div className={`score-value ${ex.status}`}>{ex.score}%</div>
                          ) : null}
                          <div className={`form-label ${ex.status}`}>{ex.form}</div>
                        </div>
                        <div className="ex-chevron">
                          <NavArrowIcon
                            style={{
                              transform:
                                expandedExercise === index ? 'rotate(-90deg)' : 'rotate(90deg)',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {expandedExercise === index && (
                      <div className="exercise-details">
                        <div className="details-grid">
                          <div className="details-box">
                            <h3 className="box-title">Body Tracking</h3>
                            <div className="tracking-list">
                              {(
                                ex.details?.bodyTracking || [
                                  {
                                    label: 'Posture',
                                    value: 'No Data',
                                    status: 'no-data',
                                  },
                                  {
                                    label: 'Depth',
                                    value: 'No Data',
                                    status: 'no-data',
                                  },
                                  {
                                    label: 'Knee Alignment',
                                    value: 'No Data',
                                    status: 'no-data',
                                  },
                                  {
                                    label: 'Core Stability',
                                    value: 'No Data',
                                    status: 'no-data',
                                  },
                                ]
                              ).map((tr: any, i: number) => {
                                const renderIcon = () => {
                                  if (tr.icon) return tr.icon;
                                  switch (tr.label) {
                                    case 'Posture':
                                      return <PostureIcon />;
                                    case 'Depth':
                                      return <DepthIcon />;
                                    case 'Knee Alignment':
                                      return <KneeAlignmentIcon />;
                                    case 'Core Stability':
                                      return <CoreStabilityIcon />;
                                    default:
                                      return <PostureIcon />;
                                  }
                                };

                                return (
                                  <div className="tracking-item" key={i}>
                                    <div className="tr-left">
                                      {renderIcon()}
                                      <span>{tr.label}</span>
                                    </div>
                                    <div className={`tr-right ${tr.status || 'no-data'}`}>
                                      {tr.value || 'No Data'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="details-box ai-observations-nested">
                            <div className="ai-title">
                              <AiObservationIcon />
                              <h3 className="box-title">AI Observations</h3>
                            </div>
                            <p>{ex.details?.aiObservations || 'No Data'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="muscle-metrics-wrapper">
            {/* Muscle Focus */}
            <div className="wd-card muscle-focus-card">
              <div className="wd-card-header">
                <MuscleFocusIcon />
                <h2>Muscle Focus</h2>
              </div>
              <div className="wd-card-content muscle-focus-content">
                <div className="trainer-program-focus-image-wrapper static-view">
                  <img
                    src={MuscleGroupImages[bodyProfile as keyof typeof MuscleGroupImages].base}
                    className="muscle-group"
                    alt={'muscleGroup'}
                  />
                  {muscleGroups.map((group) => {
                    const muscleKey = getMuscleKey(group);
                    const imageSrc = (
                      MuscleGroupImages[bodyProfile as keyof typeof MuscleGroupImages] as any
                    )[muscleKey];
                    if (!imageSrc) return null;
                    return (
                      <img
                        key={group}
                        src={imageSrc}
                        className="muscle-group-item active"
                        alt={group}
                      />
                    );
                  })}
                </div>
                <div className="muscle-focus-labels">
                  <div className="mf-label">
                    <span className="mf-title">Primary</span> {primaryGoalTitle}
                  </div>
                  <div className="mf-label">
                    <span className="mf-title">Secondary</span> {secondaryGoalTitles}
                  </div>
                </div>
              </div>
            </div>

            {/* Workout Metrics */}
            <div className="wd-card workout-metrics-card">
              <div className="wd-card-header">
                <WorkoutMetricsIcon />
                <h2>Workout Metrics</h2>
              </div>
              <div className="wd-card-content">
                <div className="summary-item">
                  <AvgHeartRateIcon />
                  <span>Avg. Heart Rate</span>
                  <span className="value">--</span>
                </div>
                <div className="summary-item">
                  <TotalRepsIcon />
                  <span>Total Reps</span>
                  <span className="value">{workoutDetails?.workoutMetrics?.totalReps ?? 0}</span>
                </div>
                <div className="summary-item">
                  <VolumeIcon />
                  <span>Volume</span>
                  <span className="value">
                    {workoutDetails?.workoutMetrics?.volume ?? 0}{' '}
                    {workoutDetails?.workoutMetrics?.weightUnit ?? 'kg'}
                  </span>
                </div>
                <div className="summary-item">
                  <RestTimeIcon />
                  <span>Rest Time</span>
                  <span className="value">{formattedRestTime}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
